/**
 * Tema claro e escuro — a lógica, separada do React.
 *
 * ================================================================
 * POR QUE ISTO NÃO É UM COMPONENTE
 * ================================================================
 *
 * A escolha do tema precisa ser aplicada ANTES do primeiro pixel, e o React só
 * monta depois que o JavaScript do bundle carrega. Entre uma coisa e outra o
 * navegador já pintou a tela — no escuro, isso é um flash branco a cada
 * abertura.
 *
 * Por isso a aplicação acontece num script inline no `index.html`, e o que mora
 * aqui são funções puras que o script e o React compartilham. Sem elemento e
 * sem storage globais: os dois entram por parâmetro, o que torna tudo
 * testável sem navegador.
 */

export type Theme = 'claro' | 'escuro';

/**
 * A chave é DUPLICADA no `index.html`, e não há como evitar.
 *
 * O script inline roda antes de qualquer módulo existir — não pode importar
 * daqui. Se este valor mudar, o do `index.html` tem de mudar junto, senão o
 * tema salvo deixa de ser encontrado na abertura e volta o flash.
 */
export const THEME_STORAGE_KEY = 'sf.theme';

/**
 * O padrão é CLARO, e não "o que o sistema operacional usa".
 *
 * Seguir o sistema mudaria a aparência do app sozinho para quem já usa há
 * meses — decisão do usuário deste projeto. Quem quiser escuro escolhe uma vez,
 * e a escolha fica.
 */
export const DEFAULT_THEME: Theme = 'claro';

export function isTheme(value: unknown): value is Theme {
  return value === 'claro' || value === 'escuro';
}

/**
 * Lê a preferência guardada.
 *
 * Valor ausente, corrompido ou de uma versão futura cai no padrão em vez de
 * quebrar — a mesma regra do período salvo: preferência inválida não pode
 * impedir o app de subir.
 */
export function readTheme(storage: Pick<Storage, 'getItem'>): Theme {
  try {
    const stored = storage.getItem(THEME_STORAGE_KEY);

    return isTheme(stored) ? stored : DEFAULT_THEME;
  } catch {
    // `localStorage` lança quando cookies de terceiros estão bloqueados em
    // alguns navegadores. Sem tema é ruim; sem aplicação é pior.
    return DEFAULT_THEME;
  }
}

/**
 * Aplica o tema ao documento.
 *
 * Duas coisas, e as duas importam:
 *
 *   `classList`   é o que o Tailwind lê — `darkMode: 'class'` no config, e as
 *                 1.100+ variantes `dark:` já escritas dependem dela;
 *   `colorScheme` é o que o NAVEGADOR lê. Sem ele, barra de rolagem, seletor
 *                 de data e a lista aberta de um `<select>` continuam brancos
 *                 no tema escuro — são desenhados pelo sistema, e nenhuma
 *                 classe CSS os alcança.
 */
export function applyTheme(theme: Theme, root: HTMLElement): void {
  root.classList.toggle('dark', theme === 'escuro');
  root.style.colorScheme = theme === 'escuro' ? 'dark' : 'light';
}

export function otherTheme(theme: Theme): Theme {
  return theme === 'escuro' ? 'claro' : 'escuro';
}
